const { request, getAccessToken, getApiBasePath, getEmbedWorkflowId } = require('./apiClient');
const { getActiveLanguage } = require('./language');

function logPublicUiDebug(action, details) {
  if (typeof console === 'undefined' || !console.log) return;
  console.log('[public-ui][' + new Date().toISOString() + '] ' + action, details || {});
}

function loadWorkflowState() {
  return request('/workflow').then(function(result) {
    return result ? result.data : null;
  });
}

function listWorkflows() {
  return request('/workflow/list').then(function(result) {
    return result && Array.isArray(result.data) ? result.data : [];
  });
}

function loadNodeLogoCatalog(options) {
  var qs = options && options.refresh ? '?refresh=1' : '';
  return request('/workflow/logos' + qs).then(function(result) {
    return result && Array.isArray(result.urls) ? result.urls : [];
  });
}

function createWorkflow(payload) {
  return request('/workflow', {
    method: 'POST',
    body: payload || {}
  }).then(function(result) {
    return result && result.data ? result.data : null;
  });
}

function loadWorkflowStateById(workflowId) {
  return request('/workflow/' + encodeURIComponent(workflowId)).then(function(result) {
    return result ? result.data : null;
  });
}

// Like loadWorkflowStateById but keeps the server's updatedAt — used by the
// editor to baseline its local draft for conflict detection.
function loadWorkflowWithMetaById(workflowId) {
  return request('/workflow/' + encodeURIComponent(workflowId)).then(function(result) {
    if (!result || !result.data) return null;
    return { state: result.data, updatedAt: result.updatedAt || null };
  });
}

function loadPublicWorkflowState(workflowId) {
  return fetch(getApiBasePath() + '/public/workflow/' + encodeURIComponent(workflowId) + '/state')
    .then(function(response) {
      if (!response.ok) throw new Error('Workflow not found.');
      return response.json();
    })
    .then(function(result) {
      return result ? result.data : null;
    });
}

function saveWorkflowState(state) {
  return request('/workflow', {
    method: 'PUT',
    body: state
  });
}

// baseUpdatedAt (optional) = the server updatedAt the caller's draft is based
// on. When passed, the server rejects the save with a 409 if someone else
// saved a newer version in the meantime (err.status === 409 for callers).
// Resolves with the new server updatedAt so the caller can re-baseline.
function saveWorkflowStateById(workflowId, state, baseUpdatedAt) {
  var options = {
    method: 'PUT',
    body: state
  };
  if (baseUpdatedAt) {
    options.headers = { 'x-base-updated-at': String(baseUpdatedAt) };
  }
  return request('/workflow/' + encodeURIComponent(workflowId), options).then(function(result) {
    return result && result.data ? result.data.updatedAt || null : null;
  });
}

// Version history: list (metadata, newest first), read one full version, and
// restore a version back as the current state (server snapshots it as a new
// version, so restore is non-destructive).
function listWorkflowVersions(workflowId) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/versions').then(function(result) {
    return result && Array.isArray(result.data) ? result.data : [];
  });
}

function loadWorkflowVersionState(workflowId, version) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/versions/' + encodeURIComponent(version)).then(function(result) {
    return result ? result.data : null;
  });
}

function restoreWorkflowVersion(workflowId, version) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/versions/' + encodeURIComponent(version) + '/restore', {
    method: 'POST'
  }).then(function(result) {
    return result ? result.data : null;
  });
}

// HTTP request log for the workflow's public run endpoint: list the newest
// calls (summaries only), then read one call's payload + response on demand.
function listWorkflowHttpRequests(workflowId) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/http-requests').then(function(result) {
    return result && Array.isArray(result.data) ? result.data : [];
  });
}

function loadWorkflowHttpRequest(workflowId, requestId) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/http-requests/' + encodeURIComponent(requestId)).then(function(result) {
    return result ? result.data : null;
  });
}

// Live (production) version: read current live metadata, publish staging → live
// (with a chosen version number; variable values are stripped server-side), and
// load the full live state read-only for the history "view live" entry.
function getLiveVersion(workflowId) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/live').then(function(result) {
    return result ? result.data : null;
  });
}

function publishLiveVersion(workflowId, version) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/live', {
    method: 'POST',
    body: { version: version }
  }).then(function(result) {
    return result ? result.data : null;
  });
}

function loadLiveVersionState(workflowId) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/live/state').then(function(result) {
    return result ? result.data : null;
  });
}

// Save config-only edits (variable values) made in Live mode back into the live
// snapshot. Auto-bumps the patch version; returns { value, version }.
function saveLiveVersionState(workflowId, state) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/live/state', {
    method: 'PUT',
    body: state
  }).then(function(result) {
    return result ? result.data : null;
  });
}

// Switch the live version offline (runs use staging) or back online. Returns the
// updated live metadata ({ version, offline, ... }).
function setLiveOffline(workflowId, offline) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/live/offline', {
    method: 'PUT',
    body: { offline: !!offline }
  }).then(function(result) {
    return result ? result.data : null;
  });
}

// Live version history: list the last 10 published live versions (metadata), and
// load one entry's full state for read-only viewing.
function listLiveHistory(workflowId) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/live/history').then(function(result) {
    return result && Array.isArray(result.data) ? result.data : [];
  });
}

function loadLiveHistoryState(workflowId, historyId) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/live/history/' + encodeURIComponent(historyId)).then(function(result) {
    return result ? result.data : null;
  });
}

// Each node persists its last run-result snapshot (node.lastOutput) for the
// canvas display. The execution engine re-runs every node from scratch and
// never reads that snapshot, so shipping it back on Play is pure weight — a big
// product/Excel/image output can push the body past the server's 15mb cap and
// trigger a 413 ("entity too large"). Return a copy of the state with each
// node's lastOutput dropped, without mutating the live canvas state (the UI
// keeps its own copy in executionState, so the display is unaffected).
function stripNodeOutputsForRun(state) {
  if (!state || !Array.isArray(state.nodes)) return state;
  var leanNodes = state.nodes.map(function(node) {
    if (!node || node.lastOutput === undefined) return node;
    var copy = Object.assign({}, node);
    delete copy.lastOutput;
    return copy;
  });
  return Object.assign({}, state, { nodes: leanNodes });
}

function startWorkflowExecution(state, options) {
  var opts = options || {};
  var payload = Object.assign({}, stripNodeOutputsForRun(state) || {});
  if (opts.workflowId) {
    payload.workflowId = String(opts.workflowId);
  }
  if (opts.fromNodeId) {
    payload.fromNodeId = String(opts.fromNodeId);
  }
  if (opts.priorNodeOutputByName && typeof opts.priorNodeOutputByName === 'object') {
    payload.priorNodeOutputByName = opts.priorNodeOutputByName;
  }
  if (opts.priorVariableValuesByName && typeof opts.priorVariableValuesByName === 'object') {
    payload.priorVariableValuesByName = opts.priorVariableValuesByName;
  }
  if (opts.checkSilentError) {
    payload.checkSilentError = true;
  }
  if (opts.disableSilentCheck) {
    payload.disableSilentCheck = true;
  }
  if (Array.isArray(opts.skipSilentCheckNodeIds) && opts.skipSilentCheckNodeIds.length) {
    payload.skipSilentCheckNodeIds = opts.skipSilentCheckNodeIds.map(String);
  }
  return request('/workflow/executions', {
    method: 'POST',
    body: payload
  }).then(function(result) {
    return result && result.data ? result.data : null;
  });
}

function startManualNodeAutoFix(nodeId, payload) {
  return request('/workflow/nodes/' + encodeURIComponent(nodeId) + '/autofix', {
    method: 'POST',
    body: payload || {}
  }).then(function(result) {
    return result && result.data ? result.data : null;
  });
}

function putCodefix(payload) {
  return request('/codefix', {
    method: 'PUT',
    body: payload || {}
  });
}

function stopWorkflowExecution(executionId) {
  return request('/workflow/executions/' + encodeURIComponent(executionId) + '/stop', { method: 'POST' });
}

function pauseWorkflowExecution(executionId) {
  return request('/workflow/executions/' + encodeURIComponent(executionId) + '/pause', { method: 'POST' });
}

function resumeWorkflowExecution(executionId) {
  return request('/workflow/executions/' + encodeURIComponent(executionId) + '/resume', { method: 'POST' });
}

function subscribeWorkflowExecution(executionId, handlers) {
  var token = getAccessToken();
  var embedId = getEmbedWorkflowId();
  var queryParts = [];
  if (token) queryParts.push('access_token=' + encodeURIComponent(token));
  if (embedId) queryParts.push('_embed=' + encodeURIComponent(embedId));
  var query = queryParts.length ? '?' + queryParts.join('&') : '';
  var source = new EventSource(getApiBasePath() + '/workflow/executions/' + encodeURIComponent(executionId) + '/stream' + query);

  function onMessage(event) {
    if (!handlers || !handlers.onEvent) return;
    try {
      var payload = JSON.parse(event.data || '{}');
      handlers.onEvent(payload);
    } catch (error) {
      // Ignore malformed events.
    }
  }

  source.onmessage = onMessage;
  source.onopen = function(event) {
    if (handlers && handlers.onOpen) handlers.onOpen(event);
  };
  source.onerror = function(error) {
    if (handlers && handlers.onError) handlers.onError(error);
  };

  return function closeStream() {
    source.close();
  };
}

function startPublicEmbedExecution(workflowId, options) {
  var opts = options || {};
  var payload = {};
  if (opts.fromNodeId) payload.fromNodeId = String(opts.fromNodeId);
  if (opts.priorNodeOutputByName && typeof opts.priorNodeOutputByName === 'object') {
    payload.priorNodeOutputByName = opts.priorNodeOutputByName;
  }
  if (opts.priorVariableValuesByName && typeof opts.priorVariableValuesByName === 'object') {
    payload.priorVariableValuesByName = opts.priorVariableValuesByName;
  }
  return fetch(getApiBasePath() + '/public/workflow/' + encodeURIComponent(workflowId) + '/executions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  })
    .then(function(response) {
      if (!response.ok) throw new Error('Could not start workflow execution.');
      return response.json();
    })
    .then(function(result) {
      return result && result.data ? result.data : null;
    });
}

function subscribePublicEmbedExecution(executionId, handlers) {
  var url = getApiBasePath() + '/public/workflow/executions/' + encodeURIComponent(executionId) + '/stream';
  var source = new EventSource(url);

  function onMessage(event) {
    if (!handlers || !handlers.onEvent) return;
    try {
      var payload = JSON.parse(event.data || '{}');
      handlers.onEvent(payload);
    } catch (error) {
      // Ignore malformed events.
    }
  }

  source.onmessage = onMessage;
  source.onopen = function(event) {
    if (handlers && handlers.onOpen) handlers.onOpen(event);
  };
  source.onerror = function(error) {
    if (handlers && handlers.onError) handlers.onError(error);
  };

  return function closeStream() {
    source.close();
  };
}

function subscribePublicWorkflowExecution(streamCode, handlers) {
  var code = String(streamCode || '').trim();
  if (!code) {
    return function noop() {};
  }

  var url = getApiBasePath() + '/public/workflow/stream/' + encodeURIComponent(code);
  logPublicUiDebug('stream.subscribe', { streamCode: code, url: url });
  var source = new EventSource(url);

  function onMessage(event) {
    if (!handlers || !handlers.onEvent) return;
    try {
      var payload = JSON.parse(event.data || '{}');
      logPublicUiDebug('stream.message', {
        streamCode: code,
        type: payload && payload.type ? payload.type : 'unknown',
        executionId: payload && payload.executionId ? payload.executionId : '',
        eventId: payload && payload.id !== undefined ? payload.id : null
      });
      handlers.onEvent(payload);
    } catch (error) {
      logPublicUiDebug('stream.message.parse_error', {
        streamCode: code,
        raw: event && event.data ? String(event.data).slice(0, 200) : '',
        message: error && error.message ? error.message : 'Unknown parse error.'
      });
      // Ignore malformed events.
    }
  }

  source.onmessage = onMessage;
  source.onopen = function(event) {
    logPublicUiDebug('stream.open', { streamCode: code, readyState: source.readyState });
    if (handlers && handlers.onOpen) handlers.onOpen(event);
  };
  source.onerror = function(error) {
    logPublicUiDebug('stream.error', { streamCode: code, readyState: source.readyState });
    if (handlers && handlers.onError) handlers.onError(error);
  };

  return function closeStream() {
    logPublicUiDebug('stream.close', { streamCode: code });
    source.close();
  };
}

function acquireWorkflowPublicListener(workflowId, streamCode, sessionId) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/public-listener', {
    method: 'POST',
    body: {
      streamCode: String(streamCode || ''),
      sessionId: String(sessionId || '')
    }
  }).then(function(result) {
    return result && result.data ? result.data : null;
  });
}

function heartbeatWorkflowPublicListener(workflowId, sessionId) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/public-listener', {
    method: 'PUT',
    body: {
      sessionId: String(sessionId || '')
    }
  }).then(function(result) {
    return result && result.data ? result.data : null;
  });
}

function releaseWorkflowPublicListener(workflowId, sessionId) {
  return request('/workflow/' + encodeURIComponent(workflowId) + '/public-listener', {
    method: 'DELETE',
    body: {
      sessionId: String(sessionId || '')
    }
  });
}

function loadCustomObjects() {
  return request('/custom-objects').then(function(result) {
    return result && Array.isArray(result.data) ? result.data : [];
  });
}

function saveCustomObject(obj) {
  return request('/custom-objects/' + encodeURIComponent(obj.id), {
    method: 'PUT',
    body: obj
  });
}

function generateCustomObjectFromPrompt(prompt, options) {
  var body = { prompt: prompt };
  if (options) {
    if (options.agentMode) body.agentMode = true;
    if (options.context) body.context = options.context;
    if (options.responseFields) body.responseFields = options.responseFields;
  }
  return request('/custom-objects/generate', {
    method: 'POST',
    body: body
  });
}

function deleteWorkflow(workflowId) {
  return request('/workflow/' + encodeURIComponent(workflowId), {
    method: 'DELETE'
  });
}

function deleteCustomObject(id) {
  return request('/custom-objects/' + encodeURIComponent(id), {
    method: 'DELETE'
  });
}

function chatWorkflowPlan(messages, context, opts) {
  var o = opts || {};
  var body = { messages: messages, context: context || null, language: getActiveLanguage() };
  if (o.chatSessionId) body.chatSessionId = o.chatSessionId;
  return request('/workflow-planner/chat', {
    method: 'POST',
    body: body
  });
}

function streamChatWorkflowPlan(messages, context, handlers, opts) {
  var h = handlers || {};
  var o = opts || {};
  var token = getAccessToken();
  var abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
  var mode = (o.mode === 'fast' || o.mode === 'balance' || o.mode === 'smartest') ? o.mode : 'balance';

  fetch(getApiBasePath() + '/workflow-planner/chat/stream', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token ? ('Bearer ' + token) : ''
    },
    body: JSON.stringify({
      messages: messages,
      context: context || null,
      mode: mode,
      language: getActiveLanguage(),
      chatSessionId: o.chatSessionId || '',
      // Single-node mode (AI Flow Builder balloon): reuse this discovery stream
      // for one node. Source-node context rides in its own field (the planner
      // server strips unknown keys from `context`).
      singleNode: !!o.singleNode,
      singleNodeContext: o.singleNodeContext || null,
      // Per-provider account the planner should use for probes + pin onto the
      // OAuth nodes it generates. { google:"<credId>", slack:"<credId>", ... }.
      // Absent/empty for a provider = use the owner's first connected account.
      plannerCredentialIds: (o.plannerCredentialIds && typeof o.plannerCredentialIds === 'object') ? o.plannerCredentialIds : null
    }),
    signal: abortController ? abortController.signal : undefined
  }).then(function(response) {
    if (!response.ok) {
      throw new Error('Stream request failed with status ' + response.status);
    }
    var reader = response.body.getReader();
    var decoder = new TextDecoder();
    var buffer = '';

    function processChunks() {
      return reader.read().then(function(result) {
        if (result.done) {
          if (h.onClose) h.onClose();
          return;
        }
        buffer += decoder.decode(result.value, { stream: true });
        var lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (var i = 0; i < lines.length; i++) {
          var line = lines[i].trim();
          if (!line || line.startsWith(':')) continue;
          if (line.startsWith('data: ')) {
            try {
              var payload = JSON.parse(line.slice(6));
              if (h.onEvent) h.onEvent(payload);
            } catch (e) {
              // ignore malformed
            }
          }
        }
        return processChunks();
      });
    }

    return processChunks();
  }).catch(function(err) {
    if (err && err.name === 'AbortError') return;
    if (h.onError) h.onError(err);
  });

  return function abort() {
    if (abortController) abortController.abort();
  };
}

function checkTemplateCompatibility(description) {
  return request('/workflow-planner/check-templates', {
    method: 'POST',
    body: { description: description }
  });
}

function diagnoseWorkflow(workflowState) {
  return request('/workflow-doctor/diagnose', {
    method: 'POST',
    body: { workflowState: workflowState }
  }).then(function(result) {
    return result && result.data ? result.data : null;
  });
}

function saveUserAiInstruction(aiInstruction) {
  return request('/user/ai-instruction', {
    method: 'PUT',
    body: { aiInstruction: aiInstruction }
  }).then(function(result) {
    return result && result.aiInstruction !== undefined ? result.aiInstruction : '';
  });
}

module.exports = {
  saveUserAiInstruction: saveUserAiInstruction,
  listWorkflows: listWorkflows,
  loadNodeLogoCatalog: loadNodeLogoCatalog,
  createWorkflow: createWorkflow,
  loadWorkflowState: loadWorkflowState,
  loadWorkflowStateById: loadWorkflowStateById,
  loadPublicWorkflowState: loadPublicWorkflowState,
  saveWorkflowState: saveWorkflowState,
  saveWorkflowStateById: saveWorkflowStateById,
  loadWorkflowWithMetaById: loadWorkflowWithMetaById,
  listWorkflowVersions: listWorkflowVersions,
  loadWorkflowVersionState: loadWorkflowVersionState,
  restoreWorkflowVersion: restoreWorkflowVersion,
  listWorkflowHttpRequests: listWorkflowHttpRequests,
  loadWorkflowHttpRequest: loadWorkflowHttpRequest,
  getLiveVersion: getLiveVersion,
  publishLiveVersion: publishLiveVersion,
  loadLiveVersionState: loadLiveVersionState,
  saveLiveVersionState: saveLiveVersionState,
  setLiveOffline: setLiveOffline,
  listLiveHistory: listLiveHistory,
  loadLiveHistoryState: loadLiveHistoryState,
  startWorkflowExecution: startWorkflowExecution,
  startPublicEmbedExecution: startPublicEmbedExecution,
  startManualNodeAutoFix: startManualNodeAutoFix,
  putCodefix: putCodefix,
  stopWorkflowExecution: stopWorkflowExecution,
  pauseWorkflowExecution: pauseWorkflowExecution,
  resumeWorkflowExecution: resumeWorkflowExecution,
  subscribeWorkflowExecution: subscribeWorkflowExecution,
  subscribePublicEmbedExecution: subscribePublicEmbedExecution,
  subscribePublicWorkflowExecution: subscribePublicWorkflowExecution,
  acquireWorkflowPublicListener: acquireWorkflowPublicListener,
  heartbeatWorkflowPublicListener: heartbeatWorkflowPublicListener,
  releaseWorkflowPublicListener: releaseWorkflowPublicListener,
  loadCustomObjects: loadCustomObjects,
  generateCustomObjectFromPrompt: generateCustomObjectFromPrompt,
  saveCustomObject: saveCustomObject,
  deleteWorkflow: deleteWorkflow,
  deleteCustomObject: deleteCustomObject,
  chatWorkflowPlan: chatWorkflowPlan,
  streamChatWorkflowPlan: streamChatWorkflowPlan,
  checkTemplateCompatibility: checkTemplateCompatibility,
  diagnoseWorkflow: diagnoseWorkflow
};
